import * as React from 'react';

import type { ProductLookupRow } from '@graphql/queries/PerceptionModelQueries';
import { Box, Divider, Slider, Typography } from '@mui/material';

import CandidateTable from './CandidateTable';
import { MIN_PEERS, type Candidate } from './normSetUtils';

/**
 * Candidate review region: how many to consider, how many are in, and the
 * table itself. Split out of NormSetBuilder to keep that component's
 * branching manageable.
 *
 * Zero is a real setting, not a degenerate one: with token search available, a
 * norm set can be assembled entirely by hand, and turning the ranking off is
 * how you say so.
 */

const TOP_N_MARKS = [0, 50, 100, 200];

type Props = {
  candidates: Candidate[];
  lookupByName: Map<string, ProductLookupRow>;
  anchorMean: Record<string, number>;
  attributes: string[];
  included: Set<string>;
  /** every member, including custom picks the ranking never produced */
  totalIncluded: number;
  onToggle: (product: string) => void;
  onToggleAll: (checked: boolean) => void;
  topN: number;
  onTopNChange: (n: number) => void;
  loading: boolean;
};

const CandidateSection: React.FC<Props> = ({
  candidates,
  lookupByName,
  anchorMean,
  attributes,
  included,
  totalIncluded,
  onToggle,
  onToggleAll,
  topN,
  onTopNChange,
  loading,
}) => {
  // Counted against the ranking, not against total membership: custom picks are
  // not in `candidates`, so pooling them here would read as "45 of 40".
  const rankedIncluded = candidates.filter((c) =>
    included.has(c.product),
  ).length;

  return (
    <>
      <Divider />

      <Box>
        <Typography gutterBottom>
          {topN === 0
            ? 'Candidates: ranking off'
            : `Candidates: nearest ${topN}`}
        </Typography>
        <Slider
          value={topN}
          onChange={(_, v) => onTopNChange(v as number)}
          step={null}
          marks={TOP_N_MARKS.map((v) => ({ value: v, label: `${v}` }))}
          min={0}
          max={200}
          sx={{ maxWidth: 320 }}
        />
      </Box>

      <Typography variant="body2">
        {topN === 0
          ? `${totalIncluded} included`
          : `${rankedIncluded} of ${candidates.length} ranked included`}
        {totalIncluded > rankedIncluded && topN !== 0 && (
          <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
            · {totalIncluded} in total
          </Box>
        )}
        {totalIncluded < MIN_PEERS && (
          <Box component="span" sx={{ color: 'warning.main', ml: 1 }}>
            need at least {MIN_PEERS}
          </Box>
        )}
      </Typography>

      {topN === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Nearest-peer ranking is off. Build the norm set from custom candidates
          below, or raise the slider to rank against the anchors again.
        </Typography>
      ) : (
        <>
          {loading ? (
            <Typography variant="body2">Loading profiles…</Typography>
          ) : (
            <CandidateTable
              candidates={candidates}
              lookupByName={lookupByName}
              anchorMean={anchorMean}
              attributes={attributes}
              included={included}
              onToggle={onToggle}
              onToggleAll={onToggleAll}
            />
          )}
        </>
      )}
    </>
  );
};

export default CandidateSection;
